import { useEffect } from "react";
import ROUTES from "../config/routes";
import spinnakerAlgoLogoBlack from "../assets/logo/spinnakeralgo/spinnaker-black.svg";
import spinnakerAlgoLogoWhite from "../assets/logo/spinnakeralgo/spinnaker-white.svg";

export const isMainRouteCheck = (path) => {
  const mainRoutes = [
    ROUTES.HOME,
    ROUTES.ABOUT_US,
    ROUTES.SWING_VISION,
    ROUTES.Q_ALPHA_FLOW,
    ROUTES.OCTOPUS_SYNC,
    ROUTES.SENTINEL,
    ROUTES.CONTACTS,
    ROUTES.NOT_FOUND, // Added NOT_FOUND to main routes for consistency if needed
  ];
  const normalizedPath =
    path !== "/" && path.endsWith("/") ? path.slice(0, -1) : path;
  return mainRoutes.includes(normalizedPath);
};

export const navigateToPathOrSection = (
  id,
  path,
  refs, // refs is kept as it might be used by callers, though App.jsx handles scrolling
  navigate,
  currentPath,
  startPageTransition
) => {
  const targetPath = id ? `${path}#${id}` : path;
  const normalizedPath =
    path !== "/" && path.endsWith("/") ? path.slice(0, -1) : path;
  const normalizedCurrentPath =
    currentPath !== "/" && currentPath.endsWith("/")
      ? currentPath.slice(0, -1)
      : currentPath;

  if (normalizedPath !== normalizedCurrentPath) {
    // Cross-page navigation: navigate directly; App.jsx handles hash smooth scrolling
    navigate(targetPath);
  } else {
    // Same-page navigation
    if (id) {
      // Update URL hash. App.jsx's useEffect handles scrolling with offset.
      if (window.location.hash !== `#${id}`) {
        navigate(targetPath);
      } else {
        // If hash is already correct, re-trigger navigation to ensure App.jsx scroll logic runs.
        navigate(targetPath);
      }
    } else {
      // Clicking main title of current page: scroll to top, then clear hash.
      universalScrollTo(0, 0, "auto");
      if (window.location.hash || path !== currentPath) {
        navigate(path); // Clears hash or ensures correct path
      }
    }
  }
};

// Updated getNavigationMenuItems to reflect the new site structure
export const getNavigationMenuItems = () => [
  { title: "Home", path: ROUTES.HOME, type: "main" },
  { title: "About Us", path: ROUTES.ABOUT_US, type: "main" },
  {
    title: "SwingVision",
    path: ROUTES.SWING_VISION,
    type: "main",
    subItems: [
      {
        title: "About",
        path: `${ROUTES.SWING_VISION}#swing-about`,
        type: "sub",
      },
      { title: "Overview", path: `${ROUTES.SWING_VISION}#swing-overview`, type: "sub" },
      { title: "Key Features", path: `${ROUTES.SWING_VISION}#swing-features`, type: "sub" },
      { title: "Technology", path: `${ROUTES.SWING_VISION}#swing-technology`, type: "sub" },
      { title: "Infrastructure", path: `${ROUTES.SWING_VISION}#swing-infrastructure`, type: "sub" },
      { title: "Markets", path: `${ROUTES.SWING_VISION}#swing-markets`, type: "sub" },
      { title: "Integration", path: `${ROUTES.SWING_VISION}#swing-integration`, type: "sub" },
    ],
  },
  {
    title: "QAlphaFlow",
    path: ROUTES.Q_ALPHA_FLOW,
    type: "main",
    subItems: [
      {
        title: "About",
        path: `${ROUTES.Q_ALPHA_FLOW}#qalpha-about`,
        type: "sub",
      },
      { title: "Overview", path: `${ROUTES.Q_ALPHA_FLOW}#qalpha-overview`, type: "sub" },
      { title: "Key Capabilities", path: `${ROUTES.Q_ALPHA_FLOW}#qalpha-key-capabilities`, type: "sub" },
      { title: "Technology", path: `${ROUTES.Q_ALPHA_FLOW}#qalpha-technology`, type: "sub" },
      { title: "Infrastructure", path: `${ROUTES.Q_ALPHA_FLOW}#qalpha-infrastructure`, type: "sub" },
      { title: "Markets", path: `${ROUTES.Q_ALPHA_FLOW}#qalpha-markets`, type: "sub" },
    ],
  },
  {
    title: "OctopusSync",
    path: ROUTES.OCTOPUS_SYNC,
    type: "main",
    subItems: [
      {
        title: "About",
        path: `${ROUTES.OCTOPUS_SYNC}#octopus-about`,
        type: "sub",
      },
      {
        title: "Features",
        path: `${ROUTES.OCTOPUS_SYNC}#octopus-features`,
        type: "sub",
      },
    ],
  },
  {
    title: "Sentinel",
    path: ROUTES.SENTINEL,
    type: "main",
    subItems: [
      { title: "About", path: `${ROUTES.SENTINEL}#sentinel-about`, type: "sub" },
      { title: "Features", path: `${ROUTES.SENTINEL}#sentinel-features`, type: "sub" },
    ],
  },
  { title: "How to Join", path: ROUTES.HOW_TO_JOIN, type: "main" },
  { title: "Contacts", path: ROUTES.CONTACTS, type: "main" },
];

export const isActivePath = (path, currentLocation) => {
  if (!path || !currentLocation) return false;
  const normalizedPath =
    path !== "/" && path.endsWith("/") ? path.slice(0, -1) : path;
  const normalizedCurrentLocation =
    currentLocation !== "/" && currentLocation.endsWith("/")
      ? currentLocation.slice(0, -1)
      : currentLocation;
  if (normalizedPath === normalizedCurrentLocation) return true;
  return (
    normalizedPath !== "/" &&
    normalizedCurrentLocation.startsWith(normalizedPath + "/")
  );
};

export const getParentSectionPath = (path) => {
  if (!path || path === "/") return null;
  const pathParts = path.split("/").filter(Boolean);
  if (pathParts.length <= 1) return `/${pathParts[0]}`;
  // For new structure, parent is always the first segment for simplicity unless more depth is added
  return `/${pathParts[0]}`;
};

export const getSectionName = (path) => {
  const parentPath = getParentSectionPath(path);
  switch (parentPath) {
    case ROUTES.ABOUT_US:
      return "About Us";
    case ROUTES.SWING_VISION:
      return "SwingVision";
    case ROUTES.Q_ALPHA_FLOW:
      return "QAlphaFlow";
    case ROUTES.OCTOPUS_SYNC:
      return "Octopus Sync";
    case ROUTES.SENTINEL:
      return "Sentinel";
    case ROUTES.CONTACTS: // Updated from CONTACT
      return "Contacts";
    case ROUTES.NOT_FOUND:
      return "Not Found";
    default:
      return "Home"; // Covers ROUTES.HOME and any other top-level or unrecognized paths
  }
};

export const generateBreadcrumbs = (path) => {
  const breadcrumbs = [{ label: "Home", path: ROUTES.HOME }];
  if (!path || path === "/" || path === ROUTES.HOME) return breadcrumbs;

  const pathSegments = path.split("/").filter(Boolean);
  let currentPath = "";

  // Handle Not Found page
  if (path === ROUTES.NOT_FOUND) {
    breadcrumbs.push({ label: "Not Found", path: ROUTES.NOT_FOUND });
    return breadcrumbs;
  }

  pathSegments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    const label =
      getSectionName(currentPath) !== "Home"
        ? getSectionName(currentPath)
        : segment
            .split("-")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    // Ensure we don't add duplicate Home or redundant labels
    if (breadcrumbs.findIndex((b) => b.path === currentPath) === -1) {
      breadcrumbs.push({
        label: label,
        path: currentPath,
      });
    }
  });

  return breadcrumbs;
};

/**
 * Enhanced intersection observer hook for better section detection
 */
export const useIntersectionObserver = (refs, setActiveSection) => {
  useEffect(() => {
    if (!refs || Object.keys(refs).length === 0) {
      // No refs provided yet, skip observer setup
      return;
    }

    const rootEl = document.getElementById('main-scroll-container') || null;
    
    // Convert object of refs to array of {id, ref} pairs, filtering out undefined refs
    const refArray = Object.entries(refs)
      .map(([id, ref]) => ({ id, ref }))
      .filter(item => item.ref && item.ref.current);
    
    if (refArray.length === 0) {
      // No valid refs to observe
      return;
    }

    let lastActiveSection = null;

    const observer = new IntersectionObserver(
      (entries) => {
        // Filter visible entries
        const visibleEntries = entries.filter(entry => entry.isIntersecting);
        
        let activeEntry = null;

        if (visibleEntries.length > 0) {
          // Find entry with highest intersection ratio among visible
          let maxRatio = 0;
          visibleEntries.forEach((entry) => {
            if (entry.intersectionRatio > maxRatio) {
              maxRatio = entry.intersectionRatio;
              activeEntry = entry;
            }
          });
        } else {
          // No visible entries - find closest to top
          let closestToTop = null;
          let minDistance = Infinity;
          
          entries.forEach((entry) => {
            const distance = Math.abs(entry.boundingClientRect.top - 100); // Offset for nav
            if (distance < minDistance) {
              minDistance = distance;
              closestToTop = entry;
            }
          });
          
          activeEntry = closestToTop;
        }

        if (activeEntry) {
          const sectionId = activeEntry.target.getAttribute("id");
          if (sectionId !== lastActiveSection) {
            lastActiveSection = sectionId;
            setActiveSection(sectionId);
          }
        }
      },
      {
        root: rootEl,
        rootMargin: "-80px 0px -70% 0px", // Top 80px offset for fixed nav, trigger when section enters top third
        threshold: [0, 0.1, 0.25, 0.5, 0.75, 1.0],
      }
    );

    // Observe all sections
    refArray.forEach(({ ref }) => {
      if (ref?.current) {
        observer.observe(ref.current);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [refs, setActiveSection]);
};

/**
 * Get appropriate logo based on text color
 */
export const getLogo = (textColor) => {
  // If text color is white or light, use white logo
  if (
    textColor === "#ffffff" ||
    textColor === "#FFFFFF" ||
    textColor === "white"
  ) {
    return spinnakerAlgoLogoWhite;
  }
  // Default to black logo
  return spinnakerAlgoLogoBlack;
};

/**
 * Universal scroll function with smooth behavior
 */
export const universalScrollTo = (x, y, behavior = "smooth") => {
  const container = document.getElementById('main-scroll-container');
  if (container) {
    container.scrollTo({ left: x, top: y, behavior });
    return;
  }
  window.scrollTo({ left: x, top: y, behavior });
};
